import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import { authService, type User } from '../services/api';
import { applyAppBgmPreferenceFromServer } from '../audio/appBgmPreference';
import {
  applyQuizSfxPreferenceFromServer,
  getQuizSfxEnabled,
  initQuizSfxPreference,
  setQuizSfxEnabledLocal,
} from '../audio/quizSfx';
import i18n from '../utils/i18n';
type QuizSoundPreferenceContextValue = {
  /** When true, quiz SFX (correct / incorrect / reward) may play. */
  soundsEnabled: boolean;
  /** Optimistic toggle + persist to API when logged in. */
  setSoundsEnabled: (enabled: boolean) => Promise<void>;
  busy: boolean;
};

const QuizSoundPreferenceContext = createContext<QuizSoundPreferenceContextValue | null>(null);

export function QuizSoundPreferenceProvider({ children }: { children: React.ReactNode }) {
  const [soundsEnabled, setSoundsEnabledState] = useState(() => getQuizSfxEnabled());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    initQuizSfxPreference();
    setSoundsEnabledState(getQuizSfxEnabled());
    const token = localStorage.getItem('authToken');
    if (!token) return;
    authService
      .getProfile()
      .then((u: User) => {
        const en = u.quizSoundsEnabled !== false;
        applyQuizSfxPreferenceFromServer(en);
        setSoundsEnabledState(en);
        if (u.backgroundMusicEnabled !== undefined) {
          applyAppBgmPreferenceFromServer(u.backgroundMusicEnabled !== false);
          try {
            const rawUser = localStorage.getItem('user');
            if (rawUser) {
              const parsed = JSON.parse(rawUser) as Record<string, unknown>;
              localStorage.setItem(
                'user',
                JSON.stringify({ ...parsed, backgroundMusicEnabled: u.backgroundMusicEnabled })
              );
            }
          } catch {
            /* ignore */
          }
        }
      })
      .catch(() => {
        /* offline / guest */
      });
  }, []);

  const setSoundsEnabled = useCallback(async (enabled: boolean) => {
    const prev = getQuizSfxEnabled();
    setQuizSfxEnabledLocal(enabled);
    setSoundsEnabledState(enabled);
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setBusy(true);
    try {
      const user = await authService.updateQuizSounds(enabled);
      applyQuizSfxPreferenceFromServer(user.quizSoundsEnabled !== false);
      setSoundsEnabledState(user.quizSoundsEnabled !== false);
      try {
        const raw = localStorage.getItem('user');
        if (raw) {
          const parsed = JSON.parse(raw) as Record<string, unknown>;
          localStorage.setItem('user', JSON.stringify({ ...parsed, quizSoundsEnabled: user.quizSoundsEnabled }));
        }
      } catch {
        /* ignore */
      }
    } catch {
      setQuizSfxEnabledLocal(prev);
      setSoundsEnabledState(prev);
      toast.error(i18n.t('user.soundPreferenceSaveError'));
    } finally {
      setBusy(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      soundsEnabled,
      setSoundsEnabled,
      busy,
    }),
    [soundsEnabled, setSoundsEnabled, busy]
  );

  return (
    <QuizSoundPreferenceContext.Provider value={value}>{children}</QuizSoundPreferenceContext.Provider>
  );
}

export function useQuizSoundPreference(): QuizSoundPreferenceContextValue {
  const ctx = useContext(QuizSoundPreferenceContext);
  if (!ctx) {
    throw new Error('useQuizSoundPreference must be used within QuizSoundPreferenceProvider');
  }
  return ctx;
}

/** Header toggle: safe if provider missing (e.g. tests) — falls back to local module state only. */
export function useQuizSoundPreferenceOptional(): QuizSoundPreferenceContextValue | null {
  return useContext(QuizSoundPreferenceContext);
}
